import { createContext, useContext, useId, type ReactNode } from "react";

const DrawRim = createContext(false);

type Mode = "back" | "mark";

function svgId(raw: string, suffix: string): string {
  return `sk${raw.replace(/[^a-zA-Z0-9]/g, "")}${suffix}`;
}

function SkinSvg({ children }: { children: ReactNode }) {
  const rim = useContext(DrawRim);
  const clip = svgId(useId(), "rim");
  return (
    <svg
      className="bj-skin-art"
      viewBox="0 0 70 98"
      preserveAspectRatio="xMidYMid meet"
      aria-hidden="true"
    >
      {rim ? (
        <defs>
          <clipPath id={clip}>
            <rect x="6" y="6" width="58" height="86" rx="2.4" />
          </clipPath>
        </defs>
      ) : null}
      <g clipPath={rim ? `url(#${clip})` : undefined}>{children}</g>
      {rim ? (
        <rect
          x="4.5"
          y="4.5"
          width="61"
          height="89"
          rx="3.2"
          fill="none"
          stroke="var(--card-accent, currentColor)"
          strokeWidth="1.35"
        />
      ) : null}
    </svg>
  );
}

function Classic({ mode }: { mode: Mode }) {
  const damask = svgId(useId(), "c");
  return (
    <SkinSvg>
      {mode === "back" ? (
        <>
          <defs>
            <pattern
              id={damask}
              width="10"
              height="10"
              x="0"
              y="4"
              patternUnits="userSpaceOnUse"
            >
              <path
                d="M5 1.2 6.4 3.6 8.8 5 6.4 6.4 5 8.8 3.6 6.4 1.2 5 3.6 3.6 Z"
                fill="currentColor"
                fillOpacity="0.28"
              />
            </pattern>
          </defs>
          <rect x="6" y="6" width="58" height="86" fill={`url(#${damask})`} />
          <rect x="7.5" y="7.5" width="55" height="83" rx="2.2" fill="none" stroke="currentColor" strokeWidth="1.15" />
          <rect x="10.5" y="10.5" width="49" height="77" rx="1.6" fill="none" stroke="currentColor" strokeOpacity="0.7" strokeWidth="0.7" />
        </>
      ) : null}
      <ellipse cx="35" cy="49" rx="16" ry="22" fill="var(--card-back, #6b1320)" stroke="currentColor" strokeWidth="1.5" />
      <ellipse cx="35" cy="49" rx="12.5" ry="17.5" fill="none" stroke="currentColor" strokeOpacity="0.65" strokeWidth="0.7" />
      <path d="M35 34 L44 49 L35 64 L26 49 Z" fill="currentColor" />
      {([
        [14, 16],
        [56, 16],
        [14, 82],
        [56, 82],
      ] as const).map(([x, y]) => (
        <path key={`${x}-${y}`} d={`M${x} ${y - 3} L${x + 2.6} ${y} L${x} ${y + 3} L${x - 2.6} ${y} Z`} fill="currentColor" />
      ))}
    </SkinSvg>
  );
}

const CLIP_CX = 35;
const CLIP_CY = 49;

/** Brick lattice mirrored about the card center. */
function brickNodes(dx: number, dy: number, cols: number, rows: number): [number, number][] {
  const nodes: [number, number][] = [];
  for (let row = -rows; row <= rows; row++) {
    const ox = row % 2 === 0 ? 0 : dx / 2;
    for (let col = -cols; col <= cols; col++) {
      nodes.push([CLIP_CX + ox + col * dx, CLIP_CY + row * dy]);
    }
  }
  return nodes;
}

const OCEAN_SCALES = brickNodes(58 / 4, 86 / 10, 3, 6);
const FIRE_FLAMES = brickNodes(58 / 4, 86 / 6, 3, 4).map(([x, y]) => [x, y - 7] as [number, number]);
const ICE_FLAKES: [number, number][] = (() => {
  const dx = 58 / 4;
  const dy = 86 / 6;
  const nodes: [number, number][] = [];
  for (let row = -3; row <= 3; row++) {
    for (let col = -2; col <= 2; col++) {
      if (row === 0 && col === 0) continue;
      nodes.push([CLIP_CX + col * dx, CLIP_CY + row * dy]);
    }
  }
  return nodes;
})();

const LIGHTNING_FIELD = brickNodes(58 / 4, 86 / 6, 3, 4).filter(
  ([x, y]) => Math.hypot(x - CLIP_CX, (y - CLIP_CY) * 0.72) > 18,
);

const STEEL_FIELD = brickNodes(58 / 3, 86 / 5, 2, 3).filter(
  ([x, y]) => Math.hypot(x - CLIP_CX, (y - CLIP_CY) * 0.72) > 22,
);

const CRYSTAL_FIELD = brickNodes(58 / 4, 86 / 6, 3, 4).filter(
  ([x, y]) => Math.hypot(x - CLIP_CX, (y - CLIP_CY) * 0.72) > 20,
);

const DESERT_FIELD = brickNodes(58 / 4, 86 / 6, 3, 4).filter(
  ([x, y]) => Math.hypot(x - CLIP_CX, (y - CLIP_CY) * 0.72) > 22,
);

const MOUNTAIN_FIELD: [number, number][] = (
  [
    [-1, -2], [0, -2], [1, -2],
    [-1, -1], [1, -1],
    [-1, 1], [1, 1],
    [-1, 2], [0, 2], [1, 2],
  ] as const
).map(([col, row]) => [CLIP_CX + col * 19, CLIP_CY + row * 20]);

const CYBER_FIELD = brickNodes(58 / 4, 86 / 6, 3, 4).filter(
  ([x, y]) => Math.hypot(x - CLIP_CX, (y - CLIP_CY) * 0.72) > 22,
);

const ROYAL_FIELD = brickNodes(58 / 4, 86 / 6, 3, 4).filter(
  ([x, y]) => Math.hypot(x - CLIP_CX, (y - CLIP_CY) * 0.72) > 20,
);

const SHADOW_FIELD = brickNodes(58 / 4, 86 / 6, 3, 4).filter(
  ([x, y]) => Math.hypot(x - CLIP_CX, (y - CLIP_CY) * 0.72) > 20,
);

const POLSKA_FIELD = brickNodes(58 / 4, 86 / 6, 3, 4).filter(
  ([x, y]) => Math.hypot(x - CLIP_CX, (y - CLIP_CY) * 0.72) > 22,
);

function gemPoints(hw: number, hh: number): string {
  return `0,${-hh} ${hw},0 0,${hh} ${-hw},0`;
}

const GEM_SMALL = gemPoints(3.8, 5.6);
const GEM_HERO = gemPoints(13.5, 21.5);
const GEM_CORE = gemPoints(7.2, 11.5);

function hexPoints(r: number): string {
  return [0, 60, 120, 180, 240, 300]
    .map((deg) => {
      const a = (deg * Math.PI) / 180;
      return `${(Math.cos(a) * r).toFixed(2)},${(Math.sin(a) * r).toFixed(2)}`;
    })
    .join(" ");
}

const HEX_HERO = hexPoints(19.5);
const HEX_CORE = hexPoints(11.2);

function oceanWave(cx: number, cy: number) {
  return (
    <g key={`${cx},${cy}`} fill="none" stroke="currentColor" strokeLinecap="round">
      <path d={`M${cx - 8} ${cy}A8 8 0 0 1 ${cx + 8} ${cy}`} strokeWidth="0.95" />
      <path
        d={`M${cx - 5.6} ${cy}A5.6 5.6 0 0 1 ${cx + 5.6} ${cy}`}
        strokeWidth="0.7"
        strokeOpacity="0.75"
      />
      <path
        d={`M${cx - 3.2} ${cy}A3.2 3.2 0 0 1 ${cx + 3.2} ${cy}`}
        strokeWidth="0.55"
        strokeOpacity="0.55"
      />
    </g>
  );
}

function Ocean({ mode }: { mode: Mode }) {
  return (
    <SkinSvg>
      {mode === "back" ? (
        <>
          {OCEAN_SCALES.map(([x, y]) => oceanWave(x, y))}
        </>
      ) : (
        <g fill="none" stroke="currentColor" strokeLinecap="round">
          <path d="M19 56A16 16 0 0 1 51 56" strokeWidth="1.5" />
          <path d="M24 56A11 11 0 0 1 46 56" strokeWidth="1.15" strokeOpacity="0.75" />
          <path d="M29 56A6 6 0 0 1 41 56" strokeWidth="0.9" strokeOpacity="0.55" />
        </g>
      )}
    </SkinSvg>
  );
}

function flameAt(x: number, y: number, scale = 1) {
  return (
    <g key={`${x}-${y}`} transform={`translate(${x} ${y}) scale(${scale})`}>
      <path
        d="M0 0 2.1 8.6 5.4 5.8 6.2 14.6-6.2 14.6-5.2 5.8-2.1 8.6Z"
        fill="var(--card-coal, #c2410c)"
        stroke="var(--card-back-ink, #ffd09a)"
        strokeWidth="0.7"
        strokeLinejoin="round"
      />
      <path
        d="M0 1.8 1.7 8.8 4.3 6.6 5.1 13.6-5.1 13.6-4.2 6.6-1.7 8.8Z"
        fill="var(--card-accent, #e85d04)"
      />
      <path
        d="M0 4.4 1.1 9.8 2.7 8.4 3.2 12.6-3.2 12.6-2.6 8.4-1.1 9.8Z"
        fill="var(--card-ember, #ffe08a)"
      />
    </g>
  );
}

function Fire({ mode }: { mode: Mode }) {
  return (
    <SkinSvg>
      {mode === "back" ? (
        <>
          {FIRE_FLAMES.map(([x, y]) => flameAt(x, y))}
        </>
      ) : (
        flameAt(35, 46, 1.35)
      )}
    </SkinSvg>
  );
}

function iceFlakeArms(size: number, tick: number) {
  return [0, 60, 120].map((rot) => (
    <g key={rot} transform={`rotate(${rot})`}>
      <path d={`M0 ${-size}V${size}`} />
      <path
        d={`M0 ${-size * 0.58}l${-tick} ${-tick}M0 ${-size * 0.58}l${tick} ${-tick}M0 ${size * 0.58}l${-tick} ${tick}M0 ${size * 0.58}l${tick} ${tick}`}
      />
    </g>
  ));
}

function Ice({ mode }: { mode: Mode }) {
  return (
    <SkinSvg>
      {mode === "back" ? (
        <g
          fill="none"
          stroke="currentColor"
          strokeWidth="0.7"
          strokeLinecap="round"
          strokeOpacity="0.42"
        >
          {ICE_FLAKES.map(([x, y]) => (
              <g key={`${x},${y}`} transform={`translate(${x} ${y})`}>
                {iceFlakeArms(4.4, 1.3)}
              </g>
            ))}
        </g>
      ) : null}
      <g
        fill="none"
        stroke="currentColor"
        strokeWidth="1.45"
        strokeLinecap="round"
        transform="translate(35 49)"
      >
        {iceFlakeArms(20, 4.2)}
        <circle r="2.2" fill="currentColor" stroke="none" />
      </g>
    </SkinSvg>
  );
}

function boltSilhouette(scale: number): string {
  const p = (x: number, y: number) => `${(x * scale).toFixed(2)} ${(y * scale).toFixed(2)}`;
  return `M${p(2, -17.5)}L${p(-1.6, -1.2)}L${p(7.4, -2.6)}L${p(-2, 17.5)}L${p(1.8, 2.8)}L${p(-7.4, 2)}Z`;
}

function sparkZigzag() {
  return "M-1.5 -5.2 1.8 -0.7 -1.8 0.7 1.5 5.2";
}

function LightningHero({ scale = 1 }: { scale?: number }) {
  return (
    <g transform={`translate(${CLIP_CX} ${CLIP_CY})`}>
      <path
        d={boltSilhouette(scale)}
        fill="currentColor"
        stroke="var(--card-back-ink, #ffe566)"
        strokeWidth="0.7"
        strokeLinejoin="round"
      />
      <path d={boltSilhouette(scale * 0.56)} fill="var(--card-flash, #fff6b0)" />
    </g>
  );
}

function Lightning({ mode }: { mode: Mode }) {
  return (
    <SkinSvg>
      {mode === "back" ? (
        <g
          fill="none"
          stroke="currentColor"
          strokeWidth="0.85"
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeOpacity="0.42"
        >
          {LIGHTNING_FIELD.map(([x, y]) => (
            <path key={`${x},${y}`} d={sparkZigzag()} transform={`translate(${x} ${y})`} />
          ))}
        </g>
      ) : null}
      <LightningHero scale={mode === "mark" ? 1.12 : 1} />
    </SkinSvg>
  );
}

function SteelHero({ scale = 1 }: { scale?: number }) {
  return (
    <g transform={`translate(${CLIP_CX} ${CLIP_CY}) scale(${scale})`}>
      <polygon
        points={HEX_HERO}
        fill="currentColor"
        stroke="var(--card-back-ink, #e2e8ee)"
        strokeWidth="0.7"
        strokeLinejoin="round"
      />
      <polygon points={HEX_CORE} fill="var(--card-plate, #6d7884)" />
      <circle r="3.6" fill="var(--card-back, #2c333b)" stroke="currentColor" strokeWidth="1.15" />
    </g>
  );
}

function rivetAt(x: number, y: number) {
  return (
    <g key={`${x},${y}`} transform={`translate(${x} ${y})`}>
      <circle r="3.8" fill="none" />
      <circle r="1.35" fill="currentColor" stroke="none" />
    </g>
  );
}

function Steel({ mode }: { mode: Mode }) {
  return (
    <SkinSvg>
      {mode === "back" ? (
        <g
          fill="none"
          stroke="currentColor"
          strokeWidth="0.85"
          strokeOpacity="0.5"
        >
          {STEEL_FIELD.map(([x, y]) => rivetAt(x, y))}
        </g>
      ) : null}
      <SteelHero scale={mode === "mark" ? 1.12 : 1} />
    </SkinSvg>
  );
}

function CrystalHero({ scale = 1 }: { scale?: number }) {
  return (
    <g transform={`translate(${CLIP_CX} ${CLIP_CY}) scale(${scale})`}>
      <polygon
        points={GEM_HERO}
        fill="currentColor"
        stroke="var(--card-back-ink, #efe7ff)"
        strokeWidth="0.7"
        strokeLinejoin="round"
      />
      <polygon points={GEM_CORE} fill="var(--card-facet, #d4c4ff)" />
    </g>
  );
}

function Crystal({ mode }: { mode: Mode }) {
  return (
    <SkinSvg>
      {mode === "back" ? (
        <g
          fill="none"
          stroke="currentColor"
          strokeWidth="0.8"
          strokeLinejoin="round"
          strokeOpacity="0.42"
        >
          {CRYSTAL_FIELD.map(([x, y]) => (
            <polygon key={`${x},${y}`} points={GEM_SMALL} transform={`translate(${x} ${y})`} />
          ))}
        </g>
      ) : null}
      <CrystalHero scale={mode === "mark" ? 1.12 : 1} />
    </SkinSvg>
  );
}

function sunRays(count: number, inner: number, outer: number) {
  const step = 360 / count;
  return Array.from({ length: count }, (_, i) => {
    const deg = i * step;
    return (
      <line key={deg} x1="0" y1={-inner} x2="0" y2={-outer} transform={`rotate(${deg})`} />
    );
  });
}

function DesertHero({ scale = 1 }: { scale?: number }) {
  return (
    <g transform={`translate(${CLIP_CX} ${CLIP_CY}) scale(${scale})`}>
      <g stroke="currentColor" strokeLinecap="round" strokeWidth="1.4">
        {sunRays(12, 13.2, 19.2)}
      </g>
      <circle r="11.4" fill="currentColor" />
      <circle r="6.2" fill="var(--card-sun, #ffe9b0)" />
    </g>
  );
}

function Desert({ mode }: { mode: Mode }) {
  return (
    <SkinSvg>
      {mode === "back" ? (
        <g
          fill="none"
          stroke="currentColor"
          strokeWidth="0.75"
          strokeLinecap="round"
          strokeOpacity="0.42"
        >
          {DESERT_FIELD.map(([x, y]) => (
            <g key={`${x},${y}`} transform={`translate(${x} ${y})`}>
              {sunRays(8, 2.6, 4.6)}
              <circle r="1.7" />
            </g>
          ))}
        </g>
      ) : null}
      <DesertHero scale={mode === "mark" ? 1.12 : 1} />
    </SkinSvg>
  );
}

function peakTri(hw: number, h: number): string {
  return `M${-hw} ${h / 2} L0 ${-h / 2} L${hw} ${h / 2} Z`;
}

function snowCap(hw: number, h: number, frac = 0.3): string {
  const peak = -h / 2;
  const y = peak + h * frac;
  const x = hw * frac;
  return `${-x},${y} 0,${peak} ${x},${y}`;
}

function MountainPeak({
  x,
  y,
  hw,
  h,
  fill,
}: {
  x: number;
  y: number;
  hw: number;
  h: number;
  fill: string;
}) {
  return (
    <g transform={`translate(${x} ${y})`}>
      <path d={peakTri(hw, h)} fill={fill} />
      <polygon points={snowCap(hw, h)} fill="var(--card-snow, #f4f6f8)" />
    </g>
  );
}

function MountainHero({ scale = 1 }: { scale?: number }) {
  return (
    <g transform={`translate(${CLIP_CX} ${CLIP_CY + 8}) scale(${scale})`}>
      <MountainPeak x={-11} y={2} hw={11} h={26} fill="var(--card-ridge, #5a6a7a)" />
      <MountainPeak x={12} y={4} hw={10} h={22} fill="var(--card-slope, #6d7d8c)" />
      <MountainPeak x={0} y={0} hw={13} h={36} fill="currentColor" />
      <path d="M0 -18 L13 18 L0 18 Z" fill="var(--card-ridge, #5a6a7a)" fillOpacity="0.35" />
      <rect x="-24" y="17.2" width="48" height="3.2" rx="1.2" fill="var(--card-ridge, #4a5a68)" />
    </g>
  );
}

function Mountain({ mode }: { mode: Mode }) {
  return (
    <SkinSvg>
      {mode === "back" ? (
        <g>
          {MOUNTAIN_FIELD.map(([x, y]) => (
            <g key={`${x},${y}`} transform={`translate(${x} ${y})`}>
              <path d={peakTri(5.2, 9.6)} fill="currentColor" fillOpacity="0.38" />
              <polygon points={snowCap(5.2, 9.6, 0.3)} fill="var(--card-snow, #f4f6f8)" fillOpacity="0.75" />
            </g>
          ))}
        </g>
      ) : null}
      <MountainHero scale={mode === "mark" ? 1.08 : 1} />
    </SkinSvg>
  );
}

function CyberHero({ scale = 1 }: { scale?: number }) {
  return (
    <g transform={`translate(${CLIP_CX} ${CLIP_CY}) scale(${scale})`}>
      <rect
        x="-16"
        y="-16"
        width="32"
        height="32"
        fill="currentColor"
        stroke="var(--card-back-ink, #5cffb0)"
        strokeWidth="0.7"
      />
      <rect x="-9" y="-9" width="18" height="18" fill="var(--card-node, #146b48)" />
      <circle r="3.5" fill="var(--card-back, #081018)" stroke="currentColor" strokeWidth="1.15" />
    </g>
  );
}

function cyberNode(x: number, y: number) {
  return (
    <g key={`${x},${y}`} transform={`translate(${x} ${y})`}>
      <rect x="-2.6" y="-2.6" width="5.2" height="5.2" />
      <path d="M-5.4 0H-2.6M2.6 0H5.4M0 -5.4V-2.6M0 2.6V5.4" />
    </g>
  );
}

function Cyber({ mode }: { mode: Mode }) {
  return (
    <SkinSvg>
      {mode === "back" ? (
        <g
          fill="none"
          stroke="currentColor"
          strokeWidth="0.8"
          strokeLinecap="square"
          strokeOpacity="0.45"
        >
          {CYBER_FIELD.map(([x, y]) => cyberNode(x, y))}
        </g>
      ) : null}
      <CyberHero scale={mode === "mark" ? 1.12 : 1} />
    </SkinSvg>
  );
}

const JEWEL_SMALL = "0,-3.4 2.2,0 0,3.4 -2.2,0";

function RoyalHero({ scale = 1 }: { scale?: number }) {
  return (
    <g transform={`translate(${CLIP_CX} ${CLIP_CY}) scale(${scale})`}>
      <path
        d="M-14.5 5.5 -14.5 -1.5 -7 5 0 -16 7 5 14.5 -1.5 14.5 5.5Z"
        fill="currentColor"
        stroke="var(--card-back-ink, #f3d9a4)"
        strokeWidth="0.7"
        strokeLinejoin="round"
      />
      <rect x="-16" y="5.5" width="32" height="7" rx="0.9" fill="var(--card-jewel, #e8c56a)" />
      <circle cy="-16" r="2.6" fill="var(--card-jewel, #e8c56a)" stroke="currentColor" strokeWidth="0.7" />
    </g>
  );
}

function Royal({ mode }: { mode: Mode }) {
  return (
    <SkinSvg>
      {mode === "back" ? (
        <g
          fill="none"
          stroke="currentColor"
          strokeWidth="0.8"
          strokeLinejoin="round"
          strokeOpacity="0.42"
        >
          {ROYAL_FIELD.map(([x, y]) => (
            <polygon key={`${x},${y}`} points={JEWEL_SMALL} transform={`translate(${x} ${y})`} />
          ))}
        </g>
      ) : null}
      <RoyalHero scale={mode === "mark" ? 1.12 : 1} />
    </SkinSvg>
  );
}

function ShadowHero({ scale = 1 }: { scale?: number }) {
  return (
    <g transform={`translate(${CLIP_CX} ${CLIP_CY}) scale(${scale})`}>
      <path
        d="M5.5-18A18 18 0 1 0 5.5 18A13 18 0 1 1 5.5-18Z"
        fill="currentColor"
        stroke="var(--card-back-ink, #cfcfcf)"
        strokeWidth="0.7"
        strokeLinejoin="round"
      />
      <path d="M6.2-10.5A11 11 0 1 0 6.2 10.5A8 11 0 1 1 6.2-10.5Z" fill="var(--card-moon, #3a3a44)" />
    </g>
  );
}

function Shadow({ mode }: { mode: Mode }) {
  return (
    <SkinSvg>
      {mode === "back" ? (
        <g
          fill="none"
          stroke="currentColor"
          strokeWidth="0.85"
          strokeLinejoin="round"
          strokeOpacity="0.42"
        >
          {SHADOW_FIELD.map(([x, y]) => (
            <path
              key={`${x},${y}`}
              d="M1.4-4.2A4.2 4.2 0 1 0 1.4 4.2A3 4.2 0 1 1 1.4-4.2Z"
              transform={`translate(${x} ${y})`}
            />
          ))}
        </g>
      ) : null}
      <ShadowHero scale={mode === "mark" ? 1.12 : 1} />
    </SkinSvg>
  );
}

/** Lattice is CSS; gold frame is a solid CSS border; SVG is the center medallion. */
function Obsidian() {
  return (
    <SkinSvg>
      <g transform={`translate(${CLIP_CX} ${CLIP_CY})`}>
        <circle r="13" fill="#050505" />
        <circle r="13" fill="none" stroke="#d4af37" strokeWidth="2.3" />
        <circle r="8.2" fill="none" stroke="#d4af37" strokeWidth="1.3" />
        <circle r="3.5" fill="#d4af37" />
      </g>
    </SkinSvg>
  );
}

function PolskaHero({ scale = 1 }: { scale?: number }) {
  return (
    <g transform={`translate(${CLIP_CX} ${CLIP_CY}) scale(${scale})`}>
      <rect x="-15" y="-20" width="30" height="20" fill="var(--card-flag, #fff)" />
      <rect x="-15" y="0" width="30" height="20" fill="var(--card-flag-red, #b01030)" />
      <rect
        x="-15"
        y="-20"
        width="30"
        height="40"
        fill="none"
        stroke="var(--card-flag, #fff)"
        strokeWidth="1.2"
      />
    </g>
  );
}

function miniFlag(x: number, y: number) {
  return (
    <g key={`${x},${y}`} transform={`translate(${x} ${y})`}>
      <rect x="-3.4" y="-4.4" width="6.8" height="4.4" fill="var(--card-flag, #fff)" />
      <rect x="-3.4" y="0" width="6.8" height="4.4" fill="var(--card-flag-red, #b01030)" />
    </g>
  );
}

function PolskaGora({ mode }: { mode: Mode }) {
  return (
    <SkinSvg>
      {mode === "back" ? POLSKA_FIELD.map(([x, y]) => miniFlag(x, y)) : null}
      <PolskaHero scale={mode === "mark" ? 1.12 : 1} />
    </SkinSvg>
  );
}

function rotatePt(x: number, y: number, deg: number): [number, number] {
  const r = (deg * Math.PI) / 180;
  return [x * Math.cos(r) - y * Math.sin(r), x * Math.sin(r) + y * Math.cos(r)];
}

/** Palmate 7-leaflet silhouette: deep V gaps, lanceolate serrated fingers, one fill. */
function cannabisSilhouette(): string {
  const leaflets = [
    { a: -72, len: 13.5, wid: 4.1, teeth: 3 },
    { a: -48, len: 18.5, wid: 4.7, teeth: 4 },
    { a: -24, len: 23.5, wid: 5.1, teeth: 4 },
    { a: 0, len: 28, wid: 5.5, teeth: 5 },
    { a: 24, len: 23.5, wid: 5.1, teeth: 4 },
    { a: 48, len: 18.5, wid: 4.7, teeth: 4 },
    { a: 72, len: 13.5, wid: 4.1, teeth: 3 },
  ];
  const pts: [number, number][] = [];
  for (const leaf of leaflets) {
    const map = (x: number, y: number) => rotatePt(x, y, leaf.a);
    for (let i = 1; i <= leaf.teeth; i++) {
      const tNotch = (i - 0.42) / (leaf.teeth + 0.4);
      const tTooth = i / (leaf.teeth + 0.4);
      pts.push(map(-leaf.wid * 0.28 * Math.sin(Math.PI * tNotch), -leaf.len * tNotch));
      pts.push(map(-leaf.wid * Math.sin(Math.PI * tTooth), -leaf.len * tTooth));
    }
    pts.push(map(0, -leaf.len));
    for (let i = leaf.teeth; i >= 1; i--) {
      const tTooth = i / (leaf.teeth + 0.4);
      const tNotch = (i - 0.42) / (leaf.teeth + 0.4);
      pts.push(map(leaf.wid * Math.sin(Math.PI * tTooth), -leaf.len * tTooth));
      pts.push(map(leaf.wid * 0.28 * Math.sin(Math.PI * tNotch), -leaf.len * tNotch));
    }
  }
  pts.push([1.1, 9], [-1.1, 9]);
  return `M0 0 ${pts.map(([x, y]) => `L${x.toFixed(1)} ${y.toFixed(1)}`).join(" ")} Z`;
}

function Ziolo({ mode }: { mode: Mode }) {
  const scale = mode === "mark" ? 1.08 : 1;
  return (
    <SkinSvg>
      <g transform={`translate(${CLIP_CX} ${CLIP_CY + 6}) scale(${scale})`}>
        <path
          d={cannabisSilhouette()}
          fill="var(--card-frond, #7ecf4a)"
          stroke="var(--card-leaf, #0d4a1c)"
          strokeWidth="0.7"
          strokeLinejoin="round"
        />
        <path
          d="M0 8V-22"
          stroke="var(--card-leaf, #0d4a1c)"
          strokeWidth="0.85"
          strokeLinecap="round"
        />
      </g>
    </SkinSvg>
  );
}

const SKINS: Record<string, (mode: Mode) => ReactNode> = {
  classic: (mode) => <Classic mode={mode} />,
  ocean: (mode) => <Ocean mode={mode} />,
  fire: (mode) => <Fire mode={mode} />,
  ice: (mode) => <Ice mode={mode} />,
  lightning: (mode) => <Lightning mode={mode} />,
  steel: (mode) => <Steel mode={mode} />,
  crystal: (mode) => <Crystal mode={mode} />,
  desert: (mode) => <Desert mode={mode} />,
  mountain: (mode) => <Mountain mode={mode} />,
  cyber: (mode) => <Cyber mode={mode} />,
  royal: (mode) => <Royal mode={mode} />,
  shadow: (mode) => <Shadow mode={mode} />,
  obsidian: () => <Obsidian />,
  polska_gora: (mode) => <PolskaGora mode={mode} />,
  ziolo: (mode) => <Ziolo mode={mode} />,
};

function renderSkin(skinId: string, mode: Mode) {
  const render = SKINS[skinId];
  if (render) return render(mode);
  return <Classic mode={mode} />;
}

export function SkinBackArt({ skinId }: { skinId: string }) {
  return <DrawRim.Provider value={skinId !== "obsidian"}>{renderSkin(skinId, "back")}</DrawRim.Provider>;
}

export function SkinFaceMark({ skinId }: { skinId: string }) {
  return renderSkin(skinId, "mark");
}
